import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FaArrowLeft, FaDownload } from 'react-icons/fa';
import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import {
  fetchInventoryPurchaseById,
  fetchPurchaseAttachments,
  getInventoryOwnerContext,
  getPurchaseAttachmentUrl,
  isGroceryOrderPurchase,
  purchaseStoreDisplay,
  purchaseSupplierDisplay,
  uploadPurchaseAttachment,
} from './inventoryApi';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Inventory.css';
import './css/Menu.css';

const PURCHASE_PATH = '/home?tab=purchase';

const displayValue = (value) => {
  const text = value != null ? String(value).trim() : '';
  return text || '—';
};

function formatPurchaseDate(val) {
  if (!val) return '—';
  const s = String(val).trim();
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.slice(0, 10);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return s.slice(0, 16);
  }
}

function normalizePurchaseStatusForUi(status) {
  const s = String(status ?? '').trim();
  if (!s) return 'NEW';
  if (s.toLowerCase() === 'received') return 'RECEIVED';
  if (s.toUpperCase() === 'RECEIVED') return 'RECEIVED';
  if (s.toUpperCase() === 'NEW') return 'NEW';
  return s.toUpperCase();
}

function formatPrice(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `₹${n.toFixed(2)}`;
}

function hasPurchaseBeenUpdated(record) {
  const updatedAt = record?.updatedDate ?? record?.Updated_Date;
  if (updatedAt == null || String(updatedAt).trim() === '') return false;
  const createdAt = record?.createdDate ?? record?.Created_Date;
  if (createdAt == null || String(createdAt).trim() === '') return true;
  const updatedMs = new Date(updatedAt).getTime();
  const createdMs = new Date(createdAt).getTime();
  if (!Number.isNaN(updatedMs) && !Number.isNaN(createdMs) && updatedMs !== createdMs) {
    return true;
  }
  const updatedBy = record?.updatedById ?? record?.Updated_By_Id;
  const createdBy = record?.createdById ?? record?.Created_By_Id;
  return updatedBy != null && String(updatedBy) !== String(createdBy ?? '');
}

function purchaseUserDisplay(record, nameKeys, idKeys) {
  for (const key of nameKeys) {
    const name = record?.[key];
    if (name != null && String(name).trim() !== '') return String(name).trim();
  }
  for (const key of idKeys) {
    const id = record?.[key];
    if (id != null && String(id).trim() !== '') return String(id).trim();
  }
  return '—';
}

const DetailField = ({ label, value, wide = false, children }) => (
  <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
    <span className="restaurant-detail-label">{label}</span>
    {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
  </div>
);

const PurchaseDetail = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { enabledTypes: enabledServiceTypes } = useRestaurantEnabledServices();
  const owner = useMemo(() => getInventoryOwnerContext(), []);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const uploadedById = Number(user?.userId || user?.id || user?.User_Id || 0);

  const resolvedPurchaseId = useMemo(() => {
    const raw = purchaseId != null ? String(purchaseId).trim() : '';
    const idNum = Number(raw);
    return !raw || Number.isNaN(idNum) ? null : idNum;
  }, [purchaseId]);

  useEffect(() => {
    let cancelled = false;

    if (resolvedPurchaseId == null) {
      setLoading(false);
      setError('Invalid purchase id.');
      setRecord(null);
      return undefined;
    }

    (async () => {
      setLoading(true);
      setError('');
      setRecord(null);
      try {
        const data = await fetchInventoryPurchaseById(resolvedPurchaseId, owner);
        if (cancelled) return;
        if (!data || typeof data !== 'object') {
          setError('No data returned for this purchase.');
          return;
        }
        setRecord(data);
      } catch (e) {
        if (cancelled) return;
        console.error(
          'Purchase detail fetch failed:',
          e?.response?.status,
          e?.response?.data ?? e?.message
        );
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            'Failed to load purchase details.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedPurchaseId, owner]);

  const loadAttachments = useCallback(async () => {
    if (resolvedPurchaseId == null) return;
    setAttachmentsLoading(true);
    setAttachmentsError('');
    try {
      const data = await fetchPurchaseAttachments(resolvedPurchaseId, owner);
      setAttachments(Array.isArray(data?.attachments) ? data.attachments : []);
    } catch (e) {
      console.error(
        'Purchase attachments fetch failed:',
        e?.response?.status,
        e?.response?.data ?? e?.message
      );
      setAttachments([]);
      setAttachmentsError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to load attachments.'
      );
    } finally {
      setAttachmentsLoading(false);
    }
  }, [resolvedPurchaseId, owner]);

  useEffect(() => {
    if (!record || resolvedPurchaseId == null) return;
    loadAttachments();
  }, [record, resolvedPurchaseId, loadAttachments]);

  const closeUploadModal = useCallback(() => {
    if (uploadSubmitting) return;
    setShowUploadModal(false);
    setUploadError('');
    setUploadFileName('');
    setUploadFile(null);
  }, [uploadSubmitting]);

  const openUploadModal = () => {
    setUploadError('');
    setUploadFileName('');
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const handleUploadAttachment = async (e) => {
    e.preventDefault();
    setUploadError('');
    if (!resolvedPurchaseId) return;
    if (!uploadFile) {
      setUploadError('Select a file to upload.');
      return;
    }
    if (!uploadedById) {
      setUploadError('Please sign in again.');
      return;
    }
    try {
      setUploadSubmitting(true);
      await uploadPurchaseAttachment(
        resolvedPurchaseId,
        {
          fileName: uploadFileName.trim() || uploadFile.name || 'bill',
          image: uploadFile,
          uploadedById,
        },
        owner
      );
      setUploadFileName('');
      setUploadFile(null);
      setShowUploadModal(false);
      await loadAttachments();
    } catch (err) {
      setUploadError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to upload attachment.'
      );
    } finally {
      setUploadSubmitting(false);
    }
  };

  const lineItems = useMemo(
    () => (Array.isArray(record?.items) ? record.items : []),
    [record]
  );

  const totalAmount = useMemo(() => {
    const headerTotal = record?.totalAmount ?? record?.Total_Amount;
    if (headerTotal != null && headerTotal !== '') return headerTotal;
    if (lineItems.length === 0) return null;
    const sum = lineItems.reduce((acc, line) => {
      const amt = line.purchaseAmount ?? line.totalPrice;
      const n = Number(amt);
      return Number.isFinite(n) ? acc + n : acc;
    }, 0);
    return sum > 0 ||
      lineItems.some((l) => l.purchaseAmount != null || l.totalPrice != null)
      ? sum
      : null;
  }, [record, lineItems]);

  const ownerDisplay = owner.isStore
    ? record?.storeName ??
      record?.Store_Name ??
      (record?.storeId != null ? `Store ${record.storeId}` : null)
    : record?.restaurantName ??
      record?.Restaurant_Name ??
      (record?.restaurantId != null ? `Restaurant ${record.restaurantId}` : null);

  const ownerLabel = owner.isStore ? 'Store' : 'Restaurant';
  const showPurchaseUpdate = useMemo(
    () => hasPurchaseBeenUpdated(record),
    [record]
  );

  return (
    <RestaurantDashboardLayout activeTab="purchase" enabledServiceTypes={enabledServiceTypes}>
      <div className="purchase-detail-page">
        <div className="restaurant-detail-panel">
          <div className="restaurant-detail-card">
            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
              <div className="inventory-item-detail-header-row">
                <button
                  type="button"
                  className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                  onClick={() => navigate(PURCHASE_PATH)}
                  aria-label="Back to purchase"
                >
                  <FaArrowLeft aria-hidden />
                </button>
                <h3 className="restaurant-detail-section-title">
                  Purchase Details
                </h3>
              </div>
            </div>

            {loading ? (
              <p className="order-details-loading">Loading purchase details…</p>
            ) : null}

            {!loading && error ? (
              <p className="restaurant-detail-message error">{error}</p>
            ) : null}

            {!loading && !error && record ? (
              <div className="restaurant-detail-grid">
                <DetailField
                  label="Purchase ID"
                  value={record.purchaseId ?? record.Purchase_Id}
                />
                {!isGroceryOrderPurchase(record) ? (
                  <DetailField
                    label="Supplier name"
                    value={purchaseSupplierDisplay(record)}
                  />
                ) : (
                  <DetailField label="Store name" value={purchaseStoreDisplay(record)} />
                )}
                {!owner.isStore ? (
                  <>
                    {!isGroceryOrderPurchase(record) ? (
                      <DetailField
                        label="Bill number"
                        value={record.billNumber ?? record.Bill_Number}
                      />
                    ) : null}
                    <DetailField
                      label="Payment type"
                      value={record.paymentType ?? record.Payment_Type}
                    />
                  </>
                ) : null}
                <DetailField
                  label="Status"
                  value={normalizePurchaseStatusForUi(record.purchaseStatus)}
                />
                <DetailField
                  label="Total amount"
                  value={totalAmount != null ? formatPrice(totalAmount) : '—'}
                />
                <DetailField label={ownerLabel} value={ownerDisplay} />
                <DetailField
                  label="Purchased on"
                  value={formatPurchaseDate(record.purchaseDate)}
                />
                {!owner.isStore && (record.createdDate ?? record.Created_Date) ? (
                  <DetailField
                    label="Created on"
                    value={formatPurchaseDate(record.createdDate ?? record.Created_Date)}
                  />
                ) : null}
                <DetailField
                  label="Created by"
                  value={purchaseUserDisplay(
                    record,
                    ['createdByName', 'Created_By_Name'],
                    ['createdById', 'Created_By_Id']
                  )}
                />
                {showPurchaseUpdate ? (
                  <>
                    <DetailField
                      label="Updated by"
                      value={purchaseUserDisplay(
                        record,
                        ['updatedByName', 'Updated_By_Name'],
                        ['updatedById', 'Updated_By_Id']
                      )}
                    />
                    <DetailField
                      label="Updated on"
                      value={formatPurchaseDate(record.updatedDate ?? record.Updated_Date)}
                    />
                  </>
                ) : null}
                <DetailField
                  label="Expected delivery"
                  value={formatPurchaseDate(record.expDeliveryDate)}
                  wide
                />
              </div>
            ) : null}

            {!loading && !record && !error ? (
              <p className="order-details-empty">No detail data for this purchase.</p>
            ) : null}
          </div>

          {!loading && !error && record ? (
            <div className="restaurant-detail-card restaurant-detail-card--line-items">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Line items</h3>
              </div>
              {lineItems.length === 0 ? (
                <p className="inventory-items-hint">No line items for this purchase.</p>
              ) : (
                <div className="purchase-detail-lines-table-wrap">
                  <table className="purchase-detail-lines-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th className="purchase-detail-lines-table__num">Price/unit</th>
                        <th className="purchase-detail-lines-table__num">Discount %</th>
                        <th className="purchase-detail-lines-table__num">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((line) => (
                        <tr
                          key={
                            line.purchaseItemId ?? `${line.productId}-${line.quantity}`
                          }
                        >
                          <td>
                            {line.productName ||
                              line.inventoryItemId ||
                              `Product ${line.productId ?? '—'}`}
                          </td>
                          <td>
                            {line.quantity ?? '—'}
                            {line.unitSymbol ? ` ${line.unitSymbol}` : ''}
                          </td>
                          <td className="purchase-detail-lines-table__num">
                            {line.pricePerUnit != null ? formatPrice(line.pricePerUnit) : '—'}
                          </td>
                          <td className="purchase-detail-lines-table__num">
                            {line.discountPercent != null ? line.discountPercent : '—'}
                          </td>
                          <td className="purchase-detail-lines-table__num">
                            {line.purchaseAmount != null || line.totalPrice != null
                              ? formatPrice(line.purchaseAmount ?? line.totalPrice)
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {totalAmount != null ? (
                      <tfoot>
                        <tr className="purchase-detail-lines-table__total-row">
                          <td colSpan={4} className="purchase-detail-lines-table__total-label">
                            Total
                          </td>
                          <td className="purchase-detail-lines-table__total-amount">
                            {formatPrice(totalAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {!loading && !error && record ? (
            <div className="restaurant-detail-card restaurant-detail-card--attachments">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Attachments</h3>
                <button
                  type="button"
                  className="add-inventory-button"
                  onClick={openUploadModal}
                >
                  Add attachment
                </button>
              </div>

              <div className="purchase-detail-attachments-body">
                {attachmentsLoading ? (
                  <p className="inventory-items-hint">Loading attachments…</p>
                ) : null}

                {!attachmentsLoading && attachmentsError ? (
                  <p className="restaurant-detail-message error">{attachmentsError}</p>
                ) : null}

                {!attachmentsLoading && !attachmentsError && attachments.length === 0 ? (
                  <p className="inventory-items-hint">No attachments yet.</p>
                ) : null}

                {!attachmentsLoading && attachments.length > 0 ? (
                  <ul className="inventory-purchase-attachments-list">
                    {attachments.map((att) => {
                      const url = getPurchaseAttachmentUrl(att);
                      const name = att.fileName || 'Attachment';
                      return (
                        <li
                          key={att.attachmentId}
                          className="inventory-purchase-attachment-item"
                        >
                          <div className="inventory-purchase-attachment-main">
                            {url ? (
                              <a
                                href={url}
                                className="inventory-purchase-attachment-link"
                                target="_blank"
                                rel="noopener noreferrer"
                                title={name}
                              >
                                <FaDownload aria-hidden />
                                <span className="inventory-purchase-attachment-name">
                                  {name}
                                </span>
                              </a>
                            ) : (
                              <span className="inventory-purchase-attachment-name">
                                {name}
                              </span>
                            )}
                          </div>
                          <span className="inventory-purchase-attachment-meta">
                            {formatPurchaseDate(att.uploadedAt)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            </div>
          ) : null}

          {showUploadModal ? (
            <div
              className="inventory-modal-overlay"
              role="presentation"
              onClick={closeUploadModal}
            >
              <div
                className="inventory-modal inventory-purchase-attachment-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="purchase-attachment-modal-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="inventory-modal-header">
                  <h4 id="purchase-attachment-modal-title">Add attachment</h4>
                  <button
                    type="button"
                    className="menu-template-modal-close"
                    aria-label="Close"
                    onClick={closeUploadModal}
                    disabled={uploadSubmitting}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>

                <form
                  className="inventory-purchase-attachment-upload-form"
                  onSubmit={handleUploadAttachment}
                >
                  <div className="inventory-form-group">
                    <label htmlFor="purchase-attachment-name">File name</label>
                    <input
                      id="purchase-attachment-name"
                      type="text"
                      placeholder="e.g. Supplier bill"
                      value={uploadFileName}
                      onChange={(e) => setUploadFileName(e.target.value)}
                    />
                  </div>
                  <div className="inventory-form-group">
                    <label htmlFor="purchase-attachment-file">File</label>
                    <input
                      id="purchase-attachment-file"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  {uploadError ? (
                    <p className="restaurant-detail-message error">{uploadError}</p>
                  ) : null}
                  <div className="inventory-form-actions">
                    <button
                      type="button"
                      className="close-inventory-button"
                      onClick={closeUploadModal}
                      disabled={uploadSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="add-inventory-button"
                      disabled={uploadSubmitting || !uploadFile}
                    >
                      {uploadSubmitting ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </RestaurantDashboardLayout>
  );
};

export default PurchaseDetail;
